import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { GoBack, LayoutContext } from '@/_shared/components';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { components } from '@/swagger/types/surveys';
import { DynamicSurvey } from '../components/WizardSurvey/EmployeeCompleteSurvey/DynamicSurvey';
import { getTemplatesCall, postTemplateSimulateCall } from '../state/api';
import { SimulateSurveyResults } from './SimulateSurveyResults';

type Answer = components['schemas']['Answer'];

export const SimulateSurveyScreen = () => {
  const t = useTranslations('surveys.simulate');
  const { setLayout } = useContext(LayoutContext);

  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { genericResponsiveValue } = useResponsiveStyle();

  const gapXS = useThemeValue('space', 'xs');

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const {
    execute: executePostTemplateSimulate,
    loading: loadingPostTemplateSimulate,
    response: responsePostTemplateSimulate,
  } = postTemplateSimulateCall.hook();

  const { control, watch, reset } = useForm<FieldValues>({
    defaultValues: {},
  });

  const [currentStepNumber, setCurrentStepNumber] = useState(0);
  const [isSimulationCompleted, setIsSimulationCompleted] = useState(false);

  const answersRef = useRef<Answer[]>([]);

  const currentTemplate = useMemo(() => {
    return responseGetTemplates?.find(template => template.id === surveyId);
  }, [responseGetTemplates, surveyId]);

  const questions = useMemo(() => {
    if (!currentTemplate?.versions?.length) return [];
    const latestVersion = currentTemplate.versions[currentTemplate.versions.length - 1];
    return latestVersion?.questions || [];
  }, [currentTemplate]);

  const currentStep = useMemo(() => {
    if (!questions.length) return undefined;
    const currentQuestion = questions[currentStepNumber];
    if (!currentQuestion) return undefined;

    return {
      isLast: currentStepNumber === questions.length - 1,
      nextQuestion: {
        id: currentQuestion.id,
        text: currentQuestion.text,
        isRequired: currentQuestion.isRequired,
        type: currentQuestion.type,
        options: currentQuestion.options,
        maxSelections: currentQuestion.maxSelections,
        visibleTo: currentQuestion.visibleTo,
        differentialScale: currentQuestion.differentialScale,
      },
      totalSteps: questions.length,
      stepNumber: currentStepNumber,
    };
  }, [questions, currentStepNumber]);

  const dynamicData = watch();

  const isNextButtonDisabled = useMemo(() => {
    if (!currentStep?.nextQuestion) return true;
    if (currentStep.nextQuestion.isRequired) {
      const questionId = currentStep.nextQuestion.id || '';
      const value = dynamicData[questionId as keyof typeof dynamicData];
      if (!value) {
        const hasMultipleChoice = Object.keys(dynamicData).some(
          key =>
            key.startsWith(`multipleChoiceAnswer_${questionId}_`) &&
            dynamicData[key as keyof typeof dynamicData],
        );
        return !hasMultipleChoice;
      }
    }
    return false;
  }, [currentStep, dynamicData]);

  const collectCurrentAnswer = useCallback((): Answer | null => {
    if (!currentStep?.nextQuestion) return null;

    const questionId = currentStep.nextQuestion.id || '';
    const questionType = currentStep.nextQuestion.type;

    if (questionType === 'DIFFERENTIAL') {
      const value = dynamicData[questionId as keyof typeof dynamicData];
      if (value !== undefined && value !== null) {
        return {
          questionId,
          differentialValue: Number(value),
        };
      }
    } else if (questionType === 'MULTIPLE_CHOICE') {
      const selectedOptionIds: string[] = [];
      Object.keys(dynamicData).forEach(key => {
        if (
          key.startsWith(`multipleChoiceAnswer_${questionId}_`) &&
          dynamicData[key as keyof typeof dynamicData]
        ) {
          const optionId = key.replace(`multipleChoiceAnswer_${questionId}_`, '');
          selectedOptionIds.push(optionId);
        }
      });
      if (selectedOptionIds.length > 0) {
        return {
          questionId,
          selectedOptionIds,
        };
      }
    } else {
      const value = dynamicData[questionId as keyof typeof dynamicData];
      if (value) {
        return {
          questionId,
          selectedOptionIds: [String(value)],
        };
      }
    }

    return null;
  }, [currentStep, dynamicData]);

  const handleNext = useCallback(() => {
    const answer = collectCurrentAnswer();
    if (answer) {
      answersRef.current = [...answersRef.current, answer];
    }

    if (currentStep?.isLast) {
      const latestVersion = currentTemplate?.versions?.[currentTemplate.versions.length - 1];
      executePostTemplateSimulate({
        templateId: surveyId || '',
        templateVersion: latestVersion?.version,
        answers: answersRef.current,
      });
      setIsSimulationCompleted(true);
    } else {
      setCurrentStepNumber(prev => prev + 1);
    }
  }, [currentStep, collectCurrentAnswer, currentTemplate, surveyId, executePostTemplateSimulate]);

  const handlePrevious = useCallback(() => {
    if (currentStepNumber > 0) {
      setCurrentStepNumber(prev => prev - 1);
    }
  }, [currentStepNumber]);

  const handleBack = () => {
    navigate(Routes.SurveyDetail.replace(':surveyId', surveyId || ''));
  };

  useEffect(() => {
    reset({});
  }, [currentStepNumber, reset]);

  useEffect(() => {
    setLayout({ title: 'surveys' });
  }, [setLayout, t]);

  if (isSimulationCompleted) {
    return (
      <Box>
        <GoBack
          label={t('goBack', { templateName: currentTemplate?.name || '' })}
          onBack={handleBack}
        />
        <Typography variant='h2' mb='xs'>
          {t('results.title')}
        </Typography>
        <Typography variant='p3/light' mb='l'>
          {t('results.description', { templateName: currentTemplate?.name || '' })}
        </Typography>
        <SimulateSurveyResults
          loading={loadingPostTemplateSimulate}
          response={responsePostTemplateSimulate}
        />
      </Box>
    );
  }

  return (
    <Box>
      <GoBack
        label={t('goBack', { templateName: currentTemplate?.name || '' })}
        onBack={handleBack}
      />
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
      >
        <Box
          flex='1'
          textAlign='left'
          mr={genericResponsiveValue({
            breakPoints: { desktop: 'm' },
            defaultValue: 'none',
          })}
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 's',
          })}
        >
          <Typography variant='h2' mb='s'>
            {t('intro.title', { templateName: currentTemplate?.name || '' })}
          </Typography>
          <Typography variant='p2/light'>
            {t('intro.description', { templateName: currentTemplate?.name || '' })}
          </Typography>
        </Box>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          maxWidth='800px'
          mx='auto'
          flex='1'
        >
          <Card width='100%' p='m'>
            {currentStep && <DynamicSurvey currentStep={currentStep} control={control} />}
          </Card>

          <Box
            display='flex'
            justifyContent='flex-end'
            width='100%'
            mt='m'
            style={{ gap: gapXS }}
            flexDirection='row'
          >
            {currentStepNumber > 0 && (
              <Button variant='link/primary' onClick={handlePrevious}>
                {t('survey.back')}
              </Button>
            )}
            <Button variant='positive' onClick={handleNext} disabled={isNextButtonDisabled}>
              {currentStep?.isLast ? t('survey.finish') : t('survey.next')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
